import {
  Activity,
  ActivityFeedItemDefinition,
} from "@ofqual/portal-activity-feed";
import { useSelector } from "react-redux";
import { authSelector } from "../../redux/selectors";
import {
  NoteAttachmentBodyProps,
  EventAttachmentBodyProps,
  CaseAttachmentActivityBody,
  InternalMessage,
  EventActivityBodyProps,
  DialogType,
  EventCompleteActivityBodyProps,
  ShareBody,
  RecordTransfer,
} from "./types";
import { DownloadLink } from "@ofqual/portal-components/components/DownloadLink";
import { ActivityType } from "../../domain/ActivityType";
import ReactMarkdown from "react-markdown";
import React from "react";
import { Chip, useTheme, Typography } from "@material-ui/core";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import { foiService } from "../../services";
import { FoiItem } from "../../domain/FoiItem";
import { fieldLabels } from "../sections/fieldLabels";
import { differenceInDays } from "date-fns";
import {
  formatUtcDate,
  standardDateFormats,
} from "@ofqual/portal-core/core/utils/dateUtils";
import { Recipient } from "../../domain/Recipient";
import { FoiEventType } from "../../domain/FoiEventType";
import { OfqualPublicMessage, PublicMessage } from "../../redux/types";

export const buildDefinitions = (
  setFocusedActivity: (activity: Activity, activityType?: DialogType) => void,
  foiItem: FoiItem,
  canRecordCompletion: boolean,
  caseTypeSlaLookups: ReturnType<typeof useLookup>[0],
  foiEventTypeLookups: ReturnType<typeof useLookup>[1],
  citizenResponseTypeLookups: ReturnType<typeof useLookup>[2],
  formalResponseTypeLookups: ReturnType<typeof useLookup>[3]
) => {
  const AttachmentBody: React.FC<NoteAttachmentBodyProps> = ({ bodyProps }) => {
    const { body, activity } = bodyProps;
    const { token } = useSelector(authSelector);
    const buildGetBlob = (blobId: string) => () =>
      foiService.downloadActivityAttachment(foiItem.id, activity.id, blobId, {
        token,
      });

    return (
      <>
        <ul style={{ padding: 0, marginLeft: 0, marginTop: 0 }}>
          {body.documents.map((doc) => (
            <li key={doc.blobId} style={{ listStyleType: "none" }}>
              {
                <DownloadLink
                  fileName={doc.fileName}
                  getBlob={buildGetBlob(doc.blobId)}
                />
              }
            </li>
          ))}
        </ul>
      </>
    );
  };

  const EventAttachmentBody: React.FC<EventAttachmentBodyProps> = ({
    bodyProps,
  }) => {
    const { body, activity } = bodyProps;
    const { token } = useSelector(authSelector);
    const buildGetBlob = (blobId: string) => () =>
      foiService.downloadFoiEventAttachment(foiItem.id, activity.id, blobId, {
        token,
      });

    return (
      <>
        <ul style={{ padding: 0, marginLeft: 0, marginTop: 0 }}>
          {body.documents?.map((doc) => (
            <li key={doc.blobId} style={{ listStyleType: "none" }}>
              {
                <DownloadLink
                  fileName={doc.fileName}
                  getBlob={buildGetBlob(doc.blobId)}
                />
              }
            </li>
          ))}
        </ul>
      </>
    );
  };

  const noteAdded: ActivityFeedItemDefinition<CaseAttachmentActivityBody> = {
    activityType: ActivityType.FoiCreateNote,
    Body: ({ body, activity }) => (
      <>
        {body.message ? (
          <div style={{ marginBottom: 15 }}>
            <b>Subject: </b>
            <Typography variant="body1">{body.subject}</Typography>
            <br />
            <b>Note: </b>
            <ReactMarkdown>{body.message}</ReactMarkdown>
          </div>
        ) : null}
        <AttachmentBody bodyProps={{ activity, body }} />
      </>
    ),
  };

  const internalMessageAdded: ActivityFeedItemDefinition<InternalMessage> = {
    activityType: ActivityType.FoiInternalMessage,
    Body: ({ body, activity }) => (
      <>
        {body.message ? (
          <div style={{ marginBottom: 15 }}>
            <b>Subject: </b>
            <Typography>{body.subject}</Typography>
            <b>Message: </b>
            <ReactMarkdown>{body.message}</ReactMarkdown>
            <b>Recipients: </b>
            <>
              {body.upns.map((r) => (
                <Chip label={r} color="secondary" />
              ))}
            </>
          </div>
        ) : null}
        <AttachmentBody bodyProps={{ activity, body }} />
      </>
    ),
  };

  const getFoiEventOptions = (hasApproveReject: boolean) => {
    if (hasApproveReject) {
      return [
        {
          label: "Approve",
          onClick: (activity: Activity) =>
            setFocusedActivity(activity, DialogType.CompleteEventApprove),
          dataTest: "add-event-Approve-button",
        },
        {
          label: "Reject",
          onClick: (activity: Activity) =>
            setFocusedActivity(activity, DialogType.CompleteEventReject),
          dataTest: "add-event-Reject-button",
        },
      ];
    } else {
      return [
        {
          label: "Record completion",
          onClick: (activity: Activity) => {
            const activityType = DialogType.CompleteEvent;
            setFocusedActivity(activity, activityType);
          },
          dataTest: "add-event-Complete-button",
        },
      ];
    }
  };

  const foiEventAdded: ActivityFeedItemDefinition<EventActivityBodyProps> = {
    activityType: FoiEventType.NewEvent,
    Body: ({ body, activity }) => {
      const theme = useTheme();
      const meetingDate = body.meetingDate;
      const foiEventType = body.foiEventType;
      const overdueDays =
        (meetingDate && differenceInDays(new Date(), new Date(meetingDate))) ||
        0;
      const recipients = body.recipients;

      return (
        <>
          <div>
            <b>{fieldLabels.meetingDateShortLabel}: </b>
            {formatUtcDate(meetingDate, standardDateFormats.DATE_TIME)}
          </div>
          <div>
            <b>{fieldLabels.foiEventType}: </b>
            <>
              {foiEventTypeLookups.find((x) => x.id === +foiEventType)?.label ||
                ""}
            </>
          </div>
          <div>
            <b>{fieldLabels.message}: </b>
            <>{body.message}</>
          </div>
          <div>
            <b>Recipients: </b>
            <ul style={{ padding: 0, marginLeft: 0, marginTop: 0 }}>
              {recipients.map((recipient: Recipient) => (
                <li key={recipient.id} style={{ listStyleType: "none" }}>
                  {recipient.name}
                </li>
              ))}
            </ul>
          </div>

          {overdueDays > 0 && (
            <div style={{ color: theme.palette.error.main }}>
              <b>Overdue by {overdueDays} day(s)</b>
            </div>
          )}

          <EventAttachmentBody bodyProps={{ activity, body }} />
        </>
      );
    },
    dynamicOptions: canRecordCompletion
      ? (props) => getFoiEventOptions(props?.body?.foiEventType === 32)
      : (_) => [],
  };

  const foiEventCompleted: ActivityFeedItemDefinition<EventCompleteActivityBodyProps> = {
    activityType: ActivityType.FoiEventCompleted,
    Body: ({ body, activity }) => {
      const attachedDocuments = () => body.documents;

      const referenceId = activity.id;
      const meetingDate = body.meetingDate;

      const { token } = useSelector(authSelector);

      const buildGetBlob = (blobId: string) => () =>
        foiService.downloadActivityAttachment(foiItem.id, referenceId, blobId, {
          token: token,
        });

      const outcome =
        body.action === "approve"
          ? "Approved"
          : body.action === "reject"
          ? "Rejected"
          : "Completed";

      return (
        <>
          <div>
            <b>{fieldLabels.meetingDateShortLabel}</b>{" "}
            {formatUtcDate(meetingDate, standardDateFormats.DATE_TIME)}
          </div>
          <div>
            <b>{fieldLabels.foiEventType}: </b>
            <>
              {foiEventTypeLookups.find((x) => x.id === +body.foiEventType)
                ?.label || ""}
            </>
          </div>
          <div>
            <b>Outcome: </b> {outcome}
          </div>
          <div>
            <b>Note: </b>
            {body.note}
          </div>
          <div>
            <ul style={{ padding: 0, marginLeft: 0, marginTop: 0 }}>
              {attachedDocuments().map((document: any) => (
                <li key={document.blobId} style={{ listStyleType: "none" }}>
                  <DownloadLink
                    fileName={document.fileName}
                    getBlob={buildGetBlob(document.blobId)}
                  />
                </li>
              ))}
            </ul>
          </div>
        </>
      );
    },
    options: [],
  };

  const publicMessageAdded: ActivityFeedItemDefinition<PublicMessage> = {
    activityType: ActivityType.PublicEnquiryPublicMessage,
    Body: ({ body, activity }) => (
      <>
        {body.message ? (
          <div style={{ marginBottom: 15 }}>
            <b>Subject: </b>
            <Typography>{body.subject}</Typography>
            <b>Message: </b>
            <ReactMarkdown>{body.message}</ReactMarkdown>
            <b>Sender: </b>
            <>
              {body.isOfqual ? (
                <Chip label="Ofqual" color="secondary" />
              ) : (
                <Chip label="Citizen" color="secondary" />
              )}
            </>
          </div>
        ) : null}
        <AttachmentBody bodyProps={{ activity, body }} />
      </>
    ),
  };

  const ofqualPublicMessageAdded: ActivityFeedItemDefinition<OfqualPublicMessage> = {
    activityType: ActivityType.FoiPublicMessageFromOfqual,
    Body: ({ body, activity }) => {
      const responseType =
        citizenResponseTypeLookups[body.responseTypeId]?.label ?? "Unknown";
      const formalResponseType =
        body.formalResponseTypeId !== null &&
        body.formalResponseTypeId !== undefined
          ? formalResponseTypeLookups[body.formalResponseTypeId]?.label ??
            "Unknown"
          : "";
      return (
        <>
          <div style={{ marginBottom: 15 }}>
            <b>Subject: </b>
            <Typography>{body.subject}</Typography>
            <b>Response type:</b>
            <Typography>{responseType}</Typography>
            {body.formalResponseTypeId !== null &&
              body.formalResponseTypeId !== undefined && (
                <>
                  <b>Formal response type:</b>
                  <Typography>{formalResponseType}</Typography>
                </>
              )}
            <b>Message: </b>
            <ReactMarkdown>{body.message}</ReactMarkdown>
            <b>Sender: </b>
            <Chip label="Ofqual" color="secondary" />
          </div>
          {!!body.documents && (
            <AttachmentBody bodyProps={{ activity, body }} />
          )}
        </>
      );
    },
  };

  const share: ActivityFeedItemDefinition<ShareBody> = {
    activityType: ActivityType.ShareRecord,
    Body: ({ body }) => (
      <>
        <b>Shared with:</b> {body.whitelist.map((item) => `${item}`).join(",")}
      </>
    ),
    options: [],
  };

  const unShare: ActivityFeedItemDefinition<ShareBody> = {
    activityType: ActivityType.UnSharedRecord,
    Body: ({ body }) => (
      <>
        <b>UnShared with:</b>{" "}
        {body.whitelist.map((item) => `${item}`).join(",")}
      </>
    ),
    options: [],
  };

  const recordTransferred: ActivityFeedItemDefinition<RecordTransfer> = {
    activityType: ActivityType.PublicEnquiryCreatedFromFoi,
    Body: ({ body, activity }) => (
      <>
        {body.action ? (
          <div style={{ marginBottom: 15 }}>
            <b>Action: </b>
            <Typography>{body.action}</Typography>
            <b>Auth: </b>
            <Typography>{body.upn}</Typography>
          </div>
        ) : null}
      </>
    ),
  };

  return [
    noteAdded,
    internalMessageAdded,
    foiEventAdded,
    foiEventCompleted,
    publicMessageAdded,
    ofqualPublicMessageAdded,
    share,
    unShare,
    recordTransferred,
  ];
};
